import classnames from 'classnames';
import * as React from 'react';
import { connect } from 'react-redux';
import { onDynamicFormSubmit, updateDynamicFormUserData } from '../../actions/dynamicFormAction';
import { black } from '../../consts/variables';
import { AttentionIcon } from '../../components/Icons/AttentionIcon';
import { EmailCheckMark } from '../../components/Icons/EmailCheckMark';
import { __ } from '../../helpers/TranslationService';
import { validateEmail } from '../../helpers/validation';
import {
    IDynamicFormCreateField,
    IDynamicFormReducer,
    IDynamicFormUserData,
} from '../../interfaces/IDynamicForm';
import { submitDynamicForm } from '../../reducers/dynamicForm/dynamicFormMappers';
import { IPageDataReducer } from '../../reducers/pageData';
import CustomSelect from '../CustomSelect/CustomSelect';
import { Input } from './Input';
import { PhoneSelection } from './PhoneSelection';
import { SubmitButton } from './SubmitButton';
import { IRouterReducer } from '../../reducers/router';

interface StateProps {
    router: IRouterReducer;
    pageData: IPageDataReducer;
    dynamicForm: IDynamicFormReducer;
}

interface DispatchProps {
    updateDynamicFormUserData(keyAndValue: { key; value }): void;
    onDynamicFormSubmit(): void;
}

type IProps = StateProps & DispatchProps & { headline?: string; className?: string };

const FormSent = ({ headline, text }: { headline: string; text: string }) => (
    <div className="dynamicFormSent">
        <EmailCheckMark width={43} height={32} viewBox="0 0 43 32" />
        <div className="headline">{headline}</div>
        <div className="body">{text}</div>
    </div>
);

const cleanupString = (value) => value.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '');

export class DynamicFormClass extends React.Component<IProps, any> {
    constructor(props) {
        super(props);
        this.state = {
            salutation: null,
            country: null,
            product: null,
        };
    }
    handleInputChange = (key) => (event) => {
        this.props.updateDynamicFormUserData({ key, value: event.target.value || '' });
    };

    handleSelectChange = (key) => (args) => {
        this.props.updateDynamicFormUserData({ key, value: args.value });
        this.setState({ [key]: args });
    };

    handlePhoneChange = (event) => {
        let phone = event.target.value.replace(/[^\d+]+/g, '');
        if (this.props.dynamicForm.userData.phoneNumber === phone) return;
        this.props.updateDynamicFormUserData({ key: 'phoneNumber', value: phone });
    };

    handleCountryCodeChange = (value) => {
        this.props.updateDynamicFormUserData({ key: 'countryCode', value });
    };

    getField = (
        fieldName: string,
        fieldProps: string,
        createFields: IDynamicFormCreateField[],
        firstInvalidFormField: string,
        isSubmitted: boolean,
    ) => {
        const field = createFields.find(
            (item: { fieldIdentifier: string }) => item.fieldIdentifier === fieldName,
        );

        if (!field)
            return {
                status: false,
                props: {
                    value: '',
                    placeholder: '',
                    onChange: this.handleInputChange(fieldName),
                    options: [],
                    clearable: false,
                    searchable: false,
                    countryCode: null,
                    countryCodes: null,
                    onPhoneChange: this.handlePhoneChange,
                    onDDDChange: this.handleInputChange('ddd'),
                    onCountryCodeChange: this.handleCountryCodeChange,
                    portalId: null,
                },
            };

        const { dropDownChoices, defaultText } = field;
        let choices = dropDownChoices
            ? dropDownChoices
                  .split(';')
                  .filter(Boolean)
                  .map((it) => {
                      const [key, value] = it.split(':');
                      return { value: key ? key.trim() : '', label: value ? value.trim() : '' };
                  })
            : [];
        const defaultValue = { label: defaultText, value: '' };
        choices.unshift(defaultValue);

        return {
            status: true,
            props: {
                name: field.fieldIdentifier,
                placeholder: field.defaultText ?? '',
                options: choices,
                value: fieldProps,
                onChange:
                    field.fieldType === 'text' || field.fieldType === 'textArea'
                        ? this.handleInputChange(fieldName)
                        : this.handleSelectChange(fieldName),
                isError: field.required?.length ? isSubmitted && fieldProps?.length === 0 : false,
                isFirstInvalidFormField: field.required?.length
                    ? firstInvalidFormField === fieldName
                    : false,
            },
        };
    };

    getApiErrors = (errors) =>
        errors.map((error) => <div key={error}>{__(error, 'dreamlines')}</div>);

    render() {
        const { pageData, dynamicForm } = this.props;
        const {
            headline,
            userData,
            successHeadline,
            successMessage,
            errorMessage,
            createFields,
            identifier,
        }: IDynamicFormReducer = dynamicForm;
        const {
            isSubmitted,
            isSent,
            isSubmitError,
            firstInvalidFormField,
            salutation,
            firstName,
            lastName,
            address,
            addressNumber,
            postalCode,
            city,
            country,
            countryCode,
            phoneNumber,
            email,
            tripId,
            product,
            message,
            errors,
        }: IDynamicFormUserData = userData;

        const countryCodes = pageData.brandConfig.requestForm.countryCodes;
        const defaultCountryCode = countryCodes[0]?.code || '';

        if (!countryCode && defaultCountryCode) this.handleCountryCodeChange(defaultCountryCode);

        if (!identifier) return null;

        const messageHeadline = cleanupString(successHeadline);
        const messageText = cleanupString(successMessage);
        const messageError = this.getApiErrors(errors || []) || errorMessage;

        if (isSent) return <FormSent headline={messageHeadline} text={messageText} />;

        const salutationProps = {
            ...this.getField(
                'salutation',
                salutation,
                createFields,
                firstInvalidFormField,
                isSubmitted,
            ),
            value: this.state.salutation,
            clearable: false,
            searchable: false,
            wrapperStyle: {
                msGridColumn: 3,
                msGridRow: 2,
                marginLeft: 5,
                marginRight: 5,
                color: black,
            },
        };

        const firstNameProps = {
            ...this.getField(
                'firstName',
                firstName,
                createFields,
                firstInvalidFormField,
                isSubmitted,
            ),
            wrapperStyle: { msGridColumn: 1, msGridRow: 1 },
        };

        const lastNameProps = {
            ...this.getField(
                'lastName',
                lastName,
                createFields,
                firstInvalidFormField,
                isSubmitted,
            ),
            wrapperStyle: { msGridColumn: 3, msGridRow: 1 },
        };

        const addressProps = {
            ...this.getField('address', address, createFields, firstInvalidFormField, isSubmitted),
            wrapperStyle: { msGridColumn: 3, msGridRow: 1 },
        };

        const addressNumberProps = {
            ...this.getField(
                'addressNumber',
                addressNumber,
                createFields,
                firstInvalidFormField,
                isSubmitted,
            ),
            wrapperStyle: { msGridColumn: 3, msGridRow: 1 },
        };

        const postalCodeProps = {
            ...this.getField(
                'postalCode',
                postalCode,
                createFields,
                firstInvalidFormField,
                isSubmitted,
            ),
            wrapperStyle: { msGridColumn: 3, msGridRow: 1 },
        };

        const cityProps = {
            ...this.getField('city', city, createFields, firstInvalidFormField, isSubmitted),
            wrapperStyle: { msGridColumn: 3, msGridRow: 1 },
        };

        const countryProps = {
            ...this.getField('country', country, createFields, firstInvalidFormField, isSubmitted),
            value: this.state.country,
            clearable: false,
            searchable: false,
            wrapperStyle: { msGridColumn: 3, msGridRow: 2 },
        };

        const phoneSelectionProps = {
            ...this.getField(
                'phoneNumber',
                phoneNumber,
                createFields,
                firstInvalidFormField,
                isSubmitted,
            ),
            placeholder: '',
            isSubmitted,
            ddd: null,
            phoneNumber,
            countryCode: countryCode || defaultCountryCode,
            countryCodes: countryCodes,
            firstInvalidFormField: firstInvalidFormField,
            onPhoneChange: this.handlePhoneChange,
            onDDDChange: this.handleInputChange('ddd'),
            onCountryCodeChange: this.handleCountryCodeChange,
            portalId: pageData.appConfig.portalId,
            wrapperStyle: { msGridColumn: 3, msGridRow: 1 },
        };
        const phoneSelectionWrapperStyle: any = { msGridColumn: 1, msGridRow: 2 };
        const invalidEmailWrapperStyle: any = { margin: '-15px 0 15px 15px' };

        const isEmailError = isSubmitted && !validateEmail(email);

        const emailProps = {
            ...this.getField('email', email, createFields, firstInvalidFormField, isSubmitted),
            props: {
                ...this.getField('email', email, createFields, firstInvalidFormField, isSubmitted)
                    .props,
                isError: isEmailError,
            },
            type: 'email',
            wrapperStyle: { msGridColumn: 3, msGridRow: 2 },
        };

        const tripIdProps = {
            ...this.getField('tripId', tripId, createFields, firstInvalidFormField, isSubmitted),
            wrapperStyle: { msGridColumn: 3, msGridRow: 1 },
        };

        const productProps = {
            ...this.getField('product', product, createFields, firstInvalidFormField, isSubmitted),
            value: this.state.product,
            clearable: false,
            searchable: false,
            wrapperStyle: { msGridColumn: 3, msGridRow: 2 },
        };

        const messageProps = {
            ...this.getField('message', message, createFields, firstInvalidFormField, isSubmitted),
            wrapperStyle: { msGridColumn: 3, msGridRow: 1 },
        };

        const isDynamicFormValid = submitDynamicForm(createFields, userData).isValid;

        const headlineKey = __(
            'Are you in interested in a %s(destination,cruise line,ship) cruise? We give you a free personal consultancy',
            'dreamlines',
        );
        const headlineForm = headline ? headline : headlineKey.replace('%s', this.props.headline);
        const wrapperClasses = classnames('dynamicFormWrapper', this.props.className);

        const tracking = {
            category: this.props.router.pageType,
            action: 'request information',
            label: `dynamic form - ${identifier.toLowerCase()}`,
        };

        return (
            <div className={wrapperClasses}>
                <div
                    className="headline"
                    dangerouslySetInnerHTML={{
                        __html: headlineForm,
                    }}
                />
                <div className="dynamicForm">
                    {salutationProps.status && <CustomSelect {...salutationProps.props} />}
                    {firstNameProps.status && <Input {...firstNameProps.props} />}
                    {lastNameProps.status && <Input {...lastNameProps.props} />}
                    {addressProps.status && <Input {...addressProps.props} />}
                    {addressNumberProps.status && <Input {...addressNumberProps.props} />}
                    {postalCodeProps.status && <Input {...postalCodeProps.props} />}
                    {cityProps.status && <Input {...cityProps.props} />}
                    {countryProps.status && <CustomSelect {...countryProps.props} />}
                    {phoneSelectionProps.status && (
                        <div style={phoneSelectionWrapperStyle}>
                            <PhoneSelection {...phoneSelectionProps} />
                        </div>
                    )}
                    {emailProps.status && (
                        <div style={phoneSelectionWrapperStyle}>
                            <Input {...emailProps.props} />
                            {isEmailError && (
                                <div className="errorText" style={invalidEmailWrapperStyle}>
                                    {__('errors.emailValid', 'dreamilines')}
                                </div>
                            )}
                        </div>
                    )}

                    {tripIdProps.status && <Input {...tripIdProps.props} />}
                    {productProps.status && <CustomSelect {...productProps.props} />}
                    {messageProps.status && <Input {...messageProps.props} />}
                    {isSubmitted && !isDynamicFormValid && (
                        <span className="errorText">
                            <AttentionIcon width={18} height={18} />
                            {__('mobile.errors.validationError', 'dreamlines')}
                        </span>
                    )}
                    {isSubmitError && messageError && (
                        <span className="errorText">
                            <AttentionIcon width={18} height={18} /> {messageError}
                        </span>
                    )}
                </div>
                <SubmitButton
                    className="submitDynamicForm"
                    onClick={this.props.onDynamicFormSubmit}
                    isFormValid={isDynamicFormValid}
                    tracking={tracking}
                />
            </div>
        );
    }
}

const mapStateToProps = (state: StateProps) => ({
    router: state.router,
    pageData: state.pageData,
    dynamicForm: state.dynamicForm,
});

const mapDispatchToProps = {
    updateDynamicFormUserData,
    submitDynamicForm,
    onDynamicFormSubmit,
};

export const DynamicForm = connect<StateProps, DispatchProps, any>(
    mapStateToProps,
    mapDispatchToProps,
)(DynamicFormClass);

export default DynamicForm;
